<template>
    <div class="batch-msku-cards">
        <!-- 横向切换：同一品名下的多个 MSKU 一次只看一个，切换即刷新对应数据 -->
        <div class="batch-msku-pager">
            <el-button size="mini" icon="el-icon-arrow-left" :disabled="busy || !hasPrev"
                aria-label="上一个 MSKU" @click="go(activeIndex - 1)" />
            <div class="batch-msku-tabs">
                <span v-for="(msku, index) in mskus" :key="msku + '-' + index" class="batch-msku-tab"
                    :class="{ 'is-active': index === activeIndex }" :title="msku" @click="go(index)">
                    {{ index + 1 }}
                </span>
            </div>
            <el-button size="mini" icon="el-icon-arrow-right" :disabled="busy || !hasNext"
                aria-label="下一个 MSKU" @click="go(activeIndex + 1)" />
            <span class="batch-msku-page-text">第 {{ activeIndex + 1 }} / {{ mskus.length }} 个</span>
            <span class="batch-msku-active-name" :title="activeMsku">{{ activeMsku }}</span>
            <div class="batch-msku-head-actions">
                <span class="batch-msku-quantity">
                    <span>采购数量</span>
                    <el-input-number :value="quantity" :min="0" :precision="0" :controls="false" size="mini"
                        :disabled="busy" style="width:64px"
                        @change="value => $emit('quantity-change', value)" />
                </span>
                <el-button v-if="!editing" type="text" size="mini" :disabled="busy"
                    @click="$emit('toggle-edit', true)">编辑</el-button>
                <el-button v-else type="primary" size="mini" :disabled="busy"
                    @click="$emit('toggle-edit', false)">完成</el-button>
            </div>
        </div>
        <!-- 展示与编辑共用同一套排版：编辑模式下点击字段值即在原位置变成可编辑框 -->
        <div class="batch-msku-card" :class="{ 'is-editing': editing }">
            <div class="batch-msku-field-grid">
                <div v-for="cell in cells" :key="cell.key" class="batch-msku-field-item">
                    <span class="batch-msku-field-label" :title="cell.source || cell.label">{{ cell.label }}</span>
                    <span v-if="!editing" class="batch-msku-field-value"
                        :class="{ 'is-empty': isEmptyText(cell.value) }">{{ cell.value }}</span>
                    <div v-else class="batch-msku-field-editor">
                        <div v-if="cell.field.field_name === 'item_package_dimensions'" class="batch-msku-dims">
                            <el-input-number :value="subValue(cell.field, 'length')" :min="0" :precision="2"
                                :controls="false" size="mini" placeholder="长"
                                @change="value => updateSub(cell.field, 'length', value)" />
                            <span class="batch-msku-x">×</span>
                            <el-input-number :value="subValue(cell.field, 'width')" :min="0" :precision="2"
                                :controls="false" size="mini" placeholder="宽"
                                @change="value => updateSub(cell.field, 'width', value)" />
                            <span class="batch-msku-x">×</span>
                            <el-input-number :value="subValue(cell.field, 'height')" :min="0" :precision="2"
                                :controls="false" size="mini" placeholder="高"
                                @change="value => updateSub(cell.field, 'height', value)" />
                            <el-select :value="subValue(cell.field, 'unit')" size="mini" placeholder="单位"
                                @change="value => updateSub(cell.field, 'unit', value)">
                                <el-option v-for="unit in unitOptions(cell.field, 'dims')" :key="unit.value"
                                    :label="unit.label" :value="unit.value" />
                            </el-select>
                        </div>
                        <div v-else-if="cell.field.field_name === 'item_package_weight'" class="batch-msku-dims">
                            <el-input-number :value="subValue(cell.field, 'value')" :min="0" :precision="2"
                                :controls="false" size="mini" placeholder="重量"
                                @change="value => updateSub(cell.field, 'value', value)" />
                            <el-select :value="subValue(cell.field, 'unit')" size="mini" placeholder="单位"
                                @change="value => updateSub(cell.field, 'unit', value)">
                                <el-option v-for="unit in unitOptions(cell.field, 'weight')" :key="unit.value"
                                    :label="unit.label" :value="unit.value" />
                            </el-select>
                        </div>
                        <el-switch v-else-if="isBoolField(cell.field)" :value="boolValue(cell.field.field_name)"
                            active-text="Yes" inactive-text="No"
                            @change="value => update(cell.field.field_name, value)" />
                        <el-select v-else-if="hasEnum(cell.field)" :value="form[cell.field.field_name]" filterable
                            allow-create default-first-option clearable :placeholder="placeholderOf(cell.field)"
                            @change="value => update(cell.field.field_name, value)">
                            <el-option v-for="option in cell.field.enum" :key="option" :label="option" :value="option" />
                        </el-select>
                        <el-input-number v-else-if="isNumberField(cell.field)" :value="form[cell.field.field_name]"
                            :controls="false" @change="value => update(cell.field.field_name, value)" />
                        <el-input v-else-if="isListField(cell.field)" :value="listText(cell.field)"
                            :placeholder="placeholderOf(cell.field)" :disabled="busy"
                            @input="value => updateList(cell.field, value)" />
                        <el-input v-else :value="scalarText(cell.field.field_name)"
                            :placeholder="placeholderOf(cell.field)" :disabled="busy"
                            @input="value => update(cell.field.field_name, value)" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
// 一个品名下多个 MSKU 的横向卡片：展示模式只读，编辑模式在同一套排版上就地编辑。
// 字段值直接写回 plan 的逐 MSKU 快照，父级按标题基础值重建主/副标题。
export default {
    name: 'BatchAsinMskuCards',
    props: {
        mskus: { type: Array, default: () => [] },
        activeIndex: { type: Number, default: 0 },
        editing: Boolean,
        rows: { type: Array, default: () => [] },
        fieldDefs: { type: Array, default: () => [] },
        rawValues: { type: Object, default: () => ({}) },
        quantity: { type: Number, default: 0 },
        busy: Boolean,
    },
    data() {
        return { form: {} };
    },
    computed: {
        activeMsku() {
            return this.mskus[this.activeIndex] || '';
        },
        hasPrev() {
            return this.activeIndex > 0;
        },
        hasNext() {
            return this.activeIndex < this.mskus.length - 1;
        },
        // 主/副标题编辑的是基础文案：编号与颜色在提交前由后端统一重建。
        editFields() {
            const fields = [
                { field_name: 'item_name', label: '主标题', placeholder: '主标题（编号自动生成）' },
                { field_name: 'title_differentiation', label: '副标题',
                    placeholder: '副标题（颜色自动追加）' },
            ];
            const seen = new Set(fields.map(field => field.field_name));
            (this.fieldDefs || []).forEach(field => {
                if (!field || !field.field_name || seen.has(field.field_name)) return;
                seen.add(field.field_name);
                fields.push(Object.assign({}, field, {
                    label: field.title_zh || field.field_name,
                }));
            });
            return fields;
        },
        // 展示与编辑用同一套格子：编辑时每格在原位置换成可编辑框，排版不变。
        cells() {
            if (!this.editing) return this.rows || [];
            return this.editFields.map(field => ({
                key: field.field_name,
                label: field.label,
                source: field.value_source
                    || (field.config_level === 'parent' ? '父体字段' : '品名字段'),
                field,
            }));
        },
    },
    watch: {
        activeIndex() { this.syncForm(); },
        rawValues: {
            handler() { if (!this.editing) this.syncForm(); },
            immediate: true,
        },
        editing(value) { if (!value) this.syncForm(); },
    },
    methods: {
        // 编辑草稿以「基础值」为口径：主/副标题不含自动编号与颜色。
        syncForm() {
            const values = this.rawValues || {};
            const form = JSON.parse(JSON.stringify(values));
            form.item_name = values.title_main_base !== undefined
                ? values.title_main_base : this.stripTitleToken(values.item_name);
            form.title_differentiation = values.title_sub_base !== undefined
                ? values.title_sub_base : this.scalarTextOf(values.title_differentiation);
            this.form = form;
        },
        stripTitleToken(value) {
            return String(value === undefined || value === null ? '' : value)
                .replace(/\s*\(A[0-9A-Za-z]+\)\s*$/, '').trim();
        },
        scalarTextOf(value) {
            if (value === undefined || value === null) return '';
            return typeof value === 'object' ? '' : String(value);
        },
        isEmptyText(value) {
            return value === undefined || value === null || String(value).trim() === ''
                || String(value).trim() === '未填写';
        },
        placeholderOf(field) {
            return field.placeholder || field.title_zh || field.field_name;
        },
        go(index) {
            if (this.busy) return;
            if (index === this.activeIndex || index < 0 || index >= this.mskus.length) return;
            this.$emit('select', index);
        },
        update(key, value) {
            if (key === 'item_name') {
                const base = this.stripTitleToken(value);
                this.$set(this.form, 'item_name', base);
                this.$emit('change', { key: 'title_main_base', value: base });
                return;
            }
            if (key === 'title_differentiation') {
                const base = value === undefined || value === null ? '' : String(value);
                this.$set(this.form, 'title_differentiation', base);
                this.$emit('change', { key: 'title_sub_base', value: base });
                return;
            }
            this.$set(this.form, key, value);
            this.$emit('change', { key, value });
        },
        subValue(field, sub) {
            const value = this.form[field.field_name];
            if (!value || typeof value !== 'object') {
                return sub === 'unit' ? this.defaultUnit(field) : undefined;
            }
            return value[sub] === undefined ? (sub === 'unit' ? this.defaultUnit(field) : undefined) : value[sub];
        },
        updateSub(field, sub, value) {
            const current = this.form[field.field_name];
            const group = Object.assign({}, (current && typeof current === 'object') ? current : {});
            if (sub === 'unit' && !group.unit) group.unit = this.defaultUnit(field);
            group[sub] = value;
            this.$set(this.form, field.field_name, group);
            this.$emit('change', { key: field.field_name, value: group });
        },
        defaultUnit(field) {
            return field.field_name === 'item_package_weight' ? 'pounds' : 'inches';
        },
        unitOptions(field, kind) {
            if (Array.isArray(field.unit_options) && field.unit_options.length) return field.unit_options;
            if (kind === 'weight') {
                return [
                    { value: 'pounds', label: 'Pounds' }, { value: 'ounces', label: 'Ounces' },
                    { value: 'kilograms', label: 'Kilograms' }, { value: 'grams', label: 'Grams' },
                ];
            }
            return [
                { value: 'inches', label: 'inches' }, { value: 'cm', label: 'cm' },
                { value: 'mm', label: 'mm' },
            ];
        },
        isBoolField(field) {
            return field.value_type === 'boolean' || field.type === 'boolean';
        },
        // 历史配置里布尔字段可能是 'Yes'/'No' 文本，开关要按同一口径判定。
        boolValue(key) {
            const value = this.form[key];
            if (typeof value === 'string') {
                return ['yes', 'true', '1', '是'].includes(value.trim().toLowerCase());
            }
            return !!value;
        },
        hasEnum(field) {
            return Array.isArray(field.enum) && field.enum.length > 0;
        },
        isNumberField(field) {
            return ['integer', 'number'].includes(field.value_type) || ['integer', 'number'].includes(field.type);
        },
        isListField(field) {
            return field.type === 'array' || Array.isArray(this.form[field.field_name]);
        },
        scalarText(key) {
            return this.scalarTextOf(this.form[key]);
        },
        // 多值字段按一项一个输入框维护；对象元素保留除 value 外的其它键（如 language_tag）。
        listParts(field) {
            const value = this.form[field.field_name];
            if (!Array.isArray(value)) {
                return value === undefined || value === null || value === '' ? [] : [String(value)];
            }
            return value.map(item => {
                if (item && typeof item === 'object') {
                    return item.value === undefined || item.value === null ? '' : String(item.value);
                }
                return item === undefined || item === null ? '' : String(item);
            });
        },
        // 多值字段编辑模式只给一个输入框：多项用 '|' 分隔（与导出/展示同一口径）。
        listText(field) {
            return this.listParts(field).join('|');
        },
        updateList(field, text) {
            const parts = String(text === undefined || text === null ? '' : text)
                .split('|').map(part => part.trim()).filter(part => part !== '');
            const current = this.form[field.field_name];
            // 对象元素保留除 value 外的其它键（如 language_tag）。
            const template = Array.isArray(current)
                ? current.find(item => item && typeof item === 'object') : null;
            const items = parts.map(part => (template
                ? Object.assign({}, template, { value: part }) : part));
            this.$set(this.form, field.field_name, items);
            this.$emit('change', { key: field.field_name, value: items });
        },
    },
};
</script>
<style scoped>
.batch-msku-pager {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 6px;
}

.batch-msku-tabs {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    overflow-x: auto;
    padding-bottom: 2px;
}

.batch-msku-tab {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    color: #606266;
    font-size: 12px;
    background: #f4f4f5;
    border: 1px solid #e4e7ed;
    border-radius: 11px;
    cursor: pointer;
}

.batch-msku-tab.is-active {
    color: #fff;
    background: #409eff;
    border-color: #409eff;
}

.batch-msku-page-text {
    color: #909399;
    font-size: 12px;
}

.batch-msku-active-name {
    flex: 1 1 220px;
    min-width: 0;
    overflow: hidden;
    color: #303133;
    font-size: 12px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.batch-msku-head-actions {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    margin-left: auto;
}

.batch-msku-quantity {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #909399;
    font-size: 12px;
}

.batch-msku-card {
    border: 1px solid #e8edf5;
    border-radius: 4px;
    background: #fff;
}

.batch-msku-card.is-editing {
    border-color: #c6e2ff;
    background: #fbfdff;
}

.batch-msku-field-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 8px 14px;
    max-height: 320px;
    overflow-y: auto;
    padding: 10px 14px;
    background: #fafafa;
}

.batch-msku-field-item {
    min-width: 0;
    line-height: 1.5;
}

.batch-msku-field-label {
    display: block;
    color: #909399;
    font-size: 11px;
}

.batch-msku-field-value {
    display: block;
    color: #303133;
    word-break: break-word;
    white-space: pre-wrap;
    font-size: 12px;
}

.batch-msku-field-value.is-empty {
    color: #c0c4cc;
}

/* 编辑模式：字段框与展示值同一位置、同一字号，未聚焦时看起来仍是文本 */
.batch-msku-field-editor {
    width: 100%;
}

.batch-msku-field-editor ::v-deep .el-input,
.batch-msku-field-editor ::v-deep .el-select,
.batch-msku-field-editor ::v-deep .el-input-number {
    width: 100%;
}

.batch-msku-field-editor ::v-deep .el-input__inner {
    height: 24px;
    padding: 0 4px;
    color: #303133;
    font-size: 12px;
    line-height: 24px;
    background: transparent;
    border-color: transparent;
    border-radius: 3px;
}

.batch-msku-field-editor ::v-deep .el-input__inner:hover {
    background: #fff;
    border-color: #dcdfe6;
}

.batch-msku-field-editor ::v-deep .el-input__inner:focus {
    background: #fff;
    border-color: #409eff;
}

.batch-msku-field-editor ::v-deep .el-input-number .el-input__inner {
    text-align: left;
}

.batch-msku-field-editor ::v-deep .el-input__icon {
    line-height: 24px;
}

.batch-msku-dims {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.batch-msku-dims ::v-deep .el-input,
.batch-msku-dims ::v-deep .el-input-number,
.batch-msku-dims ::v-deep .el-select {
    width: 84px;
}

.batch-msku-x {
    color: #909399;
}
</style>
